// Screenshots on feedback, member follow-ups, and staff ticket notes: a picker
// (click or paste) and a thumbnail strip that opens a full-size viewer.
import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ImagePlus, Loader2, X } from 'lucide-react';
import { ticketsAPI } from '../api/client';
import {
  ACCEPTED_SCREENSHOT_TYPES,
  MAX_SCREENSHOTS,
  acceptScreenshots,
} from '../lib/screenshots';
import type { TicketImage } from '../types';

// Blob URLs are cached per image so a thumbnail and its full-size view share
// one download. Signed storage URLs expire, so only blobs are kept.
const blobCache = new Map<string, string>();

function useImageSrc(url: string) {
  const [loaded, setLoaded] = useState<{ url: string; src: string; failed: boolean } | null>(null);

  useEffect(() => {
    if (blobCache.has(url)) return;
    let cancelled = false;
    ticketsAPI.imageObjectUrl(url).then((resolved) => {
      if (resolved.startsWith('blob:')) blobCache.set(url, resolved);
      if (!cancelled) setLoaded({ url, src: resolved, failed: false });
    }).catch(() => {
      if (!cancelled) setLoaded({ url, src: '', failed: true });
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  const current = loaded?.url === url ? loaded : null;
  return { src: blobCache.get(url) || current?.src || '', failed: Boolean(current?.failed) };
}

export function LocalPreview({ file, onRemove }: { file: File; onRemove: () => void }) {
  const [src, setSrc] = useState('');
  useEffect(() => {
    const reader = new FileReader();
    reader.onload = () => setSrc(String(reader.result || ''));
    reader.readAsDataURL(file);
    return () => reader.abort();
  }, [file]);

  return (
    <div className="relative">
      {src && <img src={src} alt={file.name} className="h-16 w-16 rounded-lg border object-cover" />}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${file.name}`}
        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-navy text-white flex items-center justify-center shadow"
      >
        <X size={12} />
      </button>
    </div>
  );
}

/** Attach button plus previews of what will be sent. */
export function ScreenshotPicker({
  files,
  onChange,
  disabled,
  label = 'Add screenshot',
}: {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  label?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const full = files.length >= MAX_SCREENSHOTS;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {files.map((file, index) => (
        <LocalPreview
          key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
          file={file}
          onRemove={() => onChange(files.filter((_, i) => i !== index))}
        />
      ))}
      <button
        type="button"
        disabled={disabled || full}
        onClick={() => inputRef.current?.click()}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-gray-300 text-xs font-medium text-gray-600 hover:border-navy hover:text-navy transition disabled:opacity-50"
      >
        <ImagePlus size={15} />
        {files.length > 0 ? `${files.length}/${MAX_SCREENSHOTS}` : label}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_SCREENSHOT_TYPES.join(',')}
        multiple
        className="hidden"
        onChange={(event) => {
          onChange(acceptScreenshots(files, Array.from(event.target.files || [])));
          event.target.value = '';
        }}
      />
    </div>
  );
}

type StripImage = TicketImage & { width?: number; height?: number };
type StripSize = 'sm' | 'md' | 'photo';

// A lone message photo keeps its own shape, sized from the stored dimensions
// so the bubble doesn't jump when it loads.
function SinglePhoto({ image, onOpen }: { image: StripImage; onOpen: () => void }) {
  const { src, failed } = useImageSrc(image.url);
  const ratio = image.width && image.height ? image.width / image.height : 4 / 3;
  const width = Math.round(Math.min(256, 320 * ratio));
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`View ${image.filename}`}
      style={{ width, aspectRatio: String(ratio) }}
      className="flex max-w-full items-center justify-center rounded-xl bg-off-white overflow-hidden hover:ring-2 hover:ring-gold transition"
    >
      {src ? (
        <img src={src} alt={image.filename} decoding="async" className="h-full w-full object-cover" />
      ) : failed ? (
        <span className="text-xs text-gray-400">Can't load</span>
      ) : (
        <Loader2 size={18} className="mx-auto animate-spin text-gray-300" />
      )}
    </button>
  );
}

function Thumb({
  image, onOpen, size,
}: { image: StripImage; onOpen: () => void; size: StripSize }) {
  const { src, failed } = useImageSrc(image.url);
  const box = size === 'photo' ? 'h-32 w-32' : size === 'md' ? 'h-28 w-28' : 'h-16 w-16';
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`View ${image.filename}`}
      className={`${box} rounded-lg border border-gray-200 bg-off-white overflow-hidden flex items-center justify-center shrink-0 hover:ring-2 hover:ring-gold transition`}
    >
      {src ? (
        <img src={src} alt={image.filename} decoding="async" className="h-full w-full object-cover" />
      ) : failed ? (
        <span className="text-[10px] text-gray-400 px-1">Can't load</span>
      ) : (
        <Loader2 size={16} className="animate-spin text-gray-300" />
      )}
    </button>
  );
}

function Viewer({
  images, index, onIndex, onClose,
}: { images: TicketImage[]; index: number; onIndex: (index: number) => void; onClose: () => void }) {
  const image = images[index];
  const { src, failed } = useImageSrc(image.url);
  const many = images.length > 1;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (many && event.key === 'ArrowRight') onIndex((index + 1) % images.length);
      if (many && event.key === 'ArrowLeft') onIndex((index - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, images.length, many, onClose, onIndex]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 flex flex-col" onClick={onClose}>
      <div className="flex items-center gap-3 px-4 py-3 text-white" onClick={(event) => event.stopPropagation()}>
        <p className="min-w-0 flex-1 truncate text-sm">
          {image.filename}
          {many && <span className="text-white/60"> · {index + 1} of {images.length}</span>}
        </p>
        {src && (
          <a href={src} target="_blank" rel="noreferrer" className="text-sm underline text-white/80 hover:text-white">
            Open original
          </a>
        )}
        <button type="button" onClick={onClose} aria-label="Close" className="p-1.5 rounded-lg hover:bg-white/10">
          <X size={20} />
        </button>
      </div>
      <div className="flex-1 min-h-0 flex items-center justify-center px-2 pb-4 relative">
        {many && (
          <button
            type="button"
            aria-label="Previous screenshot"
            onClick={(event) => { event.stopPropagation(); onIndex((index - 1 + images.length) % images.length); }}
            className="absolute left-2 sm:left-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20"
          >
            <ChevronLeft size={24} />
          </button>
        )}
        {src ? (
          <img
            src={src}
            alt={image.filename}
            onClick={(event) => event.stopPropagation()}
            className="max-h-full max-w-full object-contain rounded"
          />
        ) : failed ? (
          <p className="text-sm text-white/70">Could not load this screenshot.</p>
        ) : (
          <Loader2 size={28} className="animate-spin text-white/60" />
        )}
        {many && (
          <button
            type="button"
            aria-label="Next screenshot"
            onClick={(event) => { event.stopPropagation(); onIndex((index + 1) % images.length); }}
            className="absolute right-2 sm:right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20"
          >
            <ChevronRight size={24} />
          </button>
        )}
      </div>
    </div>
  );
}

/** Thumbnails of saved screenshots; clicking one opens them full size. */
export function ScreenshotStrip({
  images, size = 'sm',
}: { images: StripImage[]; size?: StripSize }) {
  const [open, setOpen] = useState<number | null>(null);
  if (images.length === 0) return null;
  return (
    <>
      {size === 'photo' && images.length === 1 ? (
        <SinglePhoto image={images[0]} onOpen={() => setOpen(0)} />
      ) : (
        <div className={size === 'photo' ? 'grid grid-cols-2 gap-1.5' : 'flex flex-wrap gap-2'}>
          {images.map((image, index) => (
            <Thumb key={image.id} image={image} size={size} onOpen={() => setOpen(index)} />
          ))}
        </div>
      )}
      {open != null && images[open] && (
        <Viewer images={images} index={open} onIndex={setOpen} onClose={() => setOpen(null)} />
      )}
    </>
  );
}
